/**
 * Simplified ElevenLabs TTS Service
 * Uses eleven_multilingual_v2 model for automatic language detection
 */

class ElevenLabsTTS {
  private baseUrl = 'https://api.elevenlabs.io/v1';
  private currentAudio: HTMLAudioElement | null = null;
  // Default multilingual voice - "Rachel" supports multiple languages well
  private defaultVoiceId = '21m00Tcm4TlvDq8ikWAM';
  // Multilingual v2 model for automatic language detection
  private model = 'eleven_multilingual_v2';

  private getApiKey(): string | null {
    return process.env.REACT_APP_ELEVENLABS_API_KEY || null;
  }

  isAvailable(): boolean {
    return this.getApiKey() !== null;
  }

  async speak(text: string): Promise<void> {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      throw new Error('TTS API key not configured');
    }

    this.stop(); // Stop any current playback

    const cleanText = this.stripHtml(text);
    if (!cleanText.trim()) {
      throw new Error('No text to speak');
    }

    const requestBody = {
      text: cleanText,
      model_id: this.model, // Uses multilingual v2 for auto language detection
      voice_settings: {
        stability: 0.5,
        similarity_boost: 0.8,
        style: 0.0,
        use_speaker_boost: true
      }
    };

    try {
      const response = await fetch(`${this.baseUrl}/text-to-speech/${this.defaultVoiceId}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`TTS error: ${response.status} - ${errorText}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      return new Promise((resolve, reject) => {
        this.currentAudio = new Audio(audioUrl);
        
        this.currentAudio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          resolve();
        };
        
        this.currentAudio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          reject(new Error('Audio playback failed'));
        };
        
        this.currentAudio.play().catch(reject);
      });
    } catch (error) {
      console.error('TTS error:', error);
      throw error;
    }
  }

  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }

  private stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}

// Export singleton instance
export const elevenLabsTTS = new ElevenLabsTTS();
export default elevenLabsTTS;